import { useCallback, useState } from 'react'
import { UseFormMethods } from 'react-hook-form'
import { IError, IShipperApplicationContact } from 'types/graphqlTypes'
import { useUpsertShipperContactMutation } from '../../graphql/UpsertShipperContactsMutation'
import { TContactKeys } from '../ShipperContactFields'
import { TShipperContactsModalSchema } from '../ShipperContactsModalSchema'

/**
 * Error message keys received from BE mapped to form keys
 */
const VALID_FIELDS: Record<string, TContactKeys> = {
  phone_or_email: 'email', // when neither was provided,
  phone: 'phone', // when an invalid phone was provided,
  email: 'email', // when an invalid email was provided,
  role: 'role', // when role was not provided,
}

interface IUseUpsertShipperContacts {
  setFormError: UseFormMethods['setError']
  shipperApplicationId: string
}

/**
 * Handles mutation and errors for updating shipper contacts in the contacts table
 */
export const useUpsertShipperContacts = ({
  setFormError,
  shipperApplicationId,
}: IUseUpsertShipperContacts) => {
  const [apiErrors, setApiErrors] = useState<string[]>([])

  const [upsertShipperContacts, { loading }] = useUpsertShipperContactMutation({
    refetchQueries: ['ShipperContacts'],
  })

  const handleError = useCallback(
    (errors: IError[]) => {
      const generalErrors: string[] = []
      errors.forEach(({ key, message }) => {
        const [, index, formKey] = key.split('.')
        const fieldName = VALID_FIELDS[formKey]
        const isFormError = formKey && fieldName
        if (isFormError) {
          setFormError(`contacts.${index}.${fieldName}`, {
            message: message.replace(`${key} `, ''),
          })
          setApiErrors(['Please see form errors'])
        } else {
          generalErrors.push(message.replaceAll('_', ' '))
        }
      })
      if (generalErrors.length) setApiErrors(generalErrors)
    },
    [setFormError, setApiErrors]
  )

  const upsertContacts = useCallback(
    (onClose: () => void, customContacts?: IShipperApplicationContact[]) => async (
      formData: TShipperContactsModalSchema
    ) => {
      const { contacts } = formData || {}
      const formContacts = customContacts || contacts
      try {
        const { data } = await upsertShipperContacts({
          variables: {
            input: {
              shipperApplicationId,
              contacts: formContacts ?? [],
            },
          },
        })

        const { errors = [] } = data?.upsertShipperContacts || {}
        if (errors.length > 0) {
          handleError(errors)
        } else onClose()
      } catch (error) {
        setApiErrors([error.message])
      }
    },
    [handleError, shipperApplicationId, upsertShipperContacts]
  )

  return {
    apiErrors,
    upsertContacts,
    isUpserting: loading,
  }
}
